<template>
  <div class="space-y-4">
    <div>
      <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Resources</h3>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Browse and read MCP resources exposed by the server
      </p>
    </div>

    <div v-if="store.isLoading" class="flex items-center justify-center py-8">
      <LoadingSpinner size="lg" />
    </div>

    <Alert v-else-if="store.error" variant="error">{{ store.error }}</Alert>

    <!-- eslint-disable-next-line asab-design-system/no-raw-button-card-markup -- dashed empty-state box, not a Card (no dashed/empty variant exists yet) -->
    <div
      v-else-if="store.resources.length === 0 && store.resourceTemplates.length === 0"
      class="text-center py-8 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700"
    >
      <p class="text-sm text-gray-500 dark:text-gray-400">No resources available</p>
    </div>

    <div v-else class="space-y-3">
      <Card
        v-for="resource in store.resources"
        :key="resource.uri"
        padding="sm"
        clickable
        @click="readResource(resource.uri)"
      >
        <div class="flex items-center justify-between">
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
              {{ resource.name }}
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-400 font-mono truncate">
              {{ resource.uri }}
            </p>
          </div>
          <span
            v-if="resource.mimeType"
            class="ml-2 px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
          >
            {{ resource.mimeType }}
          </span>
        </div>
        <p v-if="resource.description" class="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {{ resource.description }}
        </p>
      </Card>

      <div
        v-for="tmpl in store.resourceTemplates"
        :key="tmpl.uriTemplate"
        class="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3"
      >
        <p class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ tmpl.name }}</p>
        <p class="text-xs text-gray-500 dark:text-gray-400 font-mono">{{ tmpl.uriTemplate }}</p>
      </div>
    </div>

    <!-- Content viewer -->
    <Card v-if="selectedContent">
      <div class="flex items-center justify-between mb-2">
        <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ selectedUri }}</h4>
        <Button variant="ghost" size="sm" @click="selectedContent = null">Close</Button>
      </div>
      <pre
        class="text-xs overflow-x-auto max-h-64 whitespace-pre-wrap text-gray-800 dark:text-gray-200"
      >{{ selectedContent }}</pre>
    </Card>
  </div>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import { Alert, Button, Card, LoadingSpinner } from '@asabdev/asab-ui';
  import { useMcpProtocolStore } from '@/stores/mcpProtocol';
  import type { McpProtocolClient } from '@/lib/mcp/protocolClient';

  interface Props {
    client: McpProtocolClient;
  }

  const props = defineProps<Props>();
  const store = useMcpProtocolStore();

  const selectedUri = ref<string | null>(null);
  const selectedContent = ref<string | null>(null);

  const readResource = async (uri: string) => {
    try {
      const result = await store.readResource(props.client, uri);
      selectedUri.value = uri;
      const first = result.contents[0];
      selectedContent.value = first?.text ?? first?.blob ?? 'No content';
    } catch {
      selectedContent.value = 'Failed to read resource';
      selectedUri.value = uri;
    }
  };
</script>
